import { useParams, Link } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();

  const comic = comics.find((c) => c.slug === id);

  if (!comic) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950">漫画不存在</h1>
          <Link to="/" className="text-primary-500 mt-4 inline-block">返回首页</Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === comic.category);
  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: `${category?.name || '分类'}漫画`, href: `/category/${comic.category}` },
    { label: comic.title },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Book',
            name: comic.title,
            author: { '@type': 'Person', name: comic.author },
            description: comic.description,
            url: `https://073manhua.com/comic/${comic.slug}`,
            inLanguage: 'zh-CN',
            datePublished: '2026-01-01',
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: comic.rating,
              bestRating: 10,
              reviewCount: Math.floor(comic.views / 100),
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://073manhua.com' },
                { '@type': 'ListItem', position: 2, name: `${category?.name || '分类'}漫画`, item: `https://073manhua.com/category/${comic.category}` },
                { '@type': 'ListItem', position: 3, name: comic.title },
              ],
            },
          }),
        }}
      />

      <article className="pt-20 md:pt-24">
        <div className="max-w-7xl mx-auto px-4 md:px-10">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mt-6 flex flex-col md:flex-row gap-8 md:gap-12">
            <div className="md:w-72 flex-shrink-0">
              <div className="aspect-[3/4] rounded-2xl overflow-hidden border border-background-200" data-product-shop>
                <img
                  src={comic.cover}
                  alt={comic.title}
                  title={`${comic.title} - 韩国漫画免费阅读`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950">
                {comic.title}
              </h1>

              <p className="text-foreground-500 text-sm mt-2">
                作者：<strong className="text-foreground-800">{comic.author}</strong>
              </p>

              <div className="flex flex-wrap items-center gap-3 mt-4">
                <span className="flex items-center gap-1 px-3 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-semibold whitespace-nowrap">
                  <i className="ri-star-fill"></i> {comic.rating} 分
                </span>
                <span className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap ${
                  comic.status === '连载中'
                    ? 'bg-secondary-100 text-secondary-700'
                    : 'bg-background-200 text-foreground-600'
                }`}>
                  {comic.status}
                </span>
                <span className="text-foreground-400 text-sm">
                  <i className="ri-eye-line mr-1"></i>
                  {(comic.views / 10000).toFixed(0)} 万阅读
                </span>
                <span className="text-foreground-400 text-sm">
                  <i className="ri-file-list-2-line mr-1"></i>
                  共 {comic.chapters} 话
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-accent-100 text-accent-700 text-xs rounded-full whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-2">作品简介</h2>
                <p className="text-foreground-600 text-sm leading-relaxed">{comic.description}</p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Link
                  to={`/comic/${comic.slug}`}
                  className="flex items-center justify-center gap-2 px-8 py-3.5 bg-primary-500 text-white rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors"
                >
                  <i className="ri-play-circle-line text-lg"></i>
                  开始阅读
                </Link>
                <button className="flex items-center justify-center gap-2 px-8 py-3.5 border-2 border-background-300 text-foreground-700 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-background-100 transition-colors">
                  <i className="ri-bookmark-line"></i>
                  加入收藏
                </button>
                <button className="flex items-center justify-center gap-2 px-8 py-3.5 border-2 border-background-300 text-foreground-700 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-background-100 transition-colors">
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
              </div>

              <div className="mt-6 p-4 bg-background-100 rounded-xl border border-background-200">
                <div className="flex items-center gap-2">
                  <i className="ri-information-line text-accent-500"></i>
                  <p className="text-xs text-foreground-600">
                    最后更新：{comic.updateDate} · 073漫画网免费提供在线漫画阅读
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8">
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-6">
              {relatedComics.map((rc) => (
                <Link
                  key={rc.id}
                  to={`/comic/${rc.slug}`}
                  className="group"
                  data-product-shop
                >
                  <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden hover:border-accent-300 transition-all duration-300">
                    <div className="aspect-[3/4] overflow-hidden">
                      <img
                        src={rc.cover}
                        alt={rc.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="font-heading text-sm font-bold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {rc.title}
                      </h3>
                      <div className="flex items-center gap-1 mt-1">
                        <i className="ri-star-fill text-primary-500 text-xs"></i>
                        <span className="text-xs text-foreground-500">{rc.rating}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-12 mb-10 text-center">
            <Link
              to={`/category/${comic.category}`}
              className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回{category?.name || ''}漫画
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}